'use client';

import { ClearIcon, SearchIcon } from '@/_assets/icon-components';
import BranchItem from '@/_components/Branches/_components/item';
import { handleScrollToBranch } from '@/_components/Branches/_utils';
import BreadcrumbComponent from '@/_components/Breadcrumbs';
import BranchesMap from '@/_components/Map/Branches';
import { IRedux, ISavedBranch } from '@/_store/types';
import { IBranch } from '@/_types';
import { BRAND_NAMES, FEATURED_BRANDS } from '@/_utils/Constants';
import { InputGroup } from '@/components/ui/input-group';
import { SegmentedControl } from '@/components/ui/segmented-control';
import {
  Box,
  Container,
  Flex,
  HStack,
  Input,
  Text,
} from '@chakra-ui/react';
import { useTranslations } from 'next-intl';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

export default function Desktop({
  data,
  search,
  setSearch,
  activeBrandId,
  setActiveBrandId,
}: {
  data: IBranch[];
  search: string;
  setSearch: (e: any) => void;
  activeBrandId: string | undefined;
  setActiveBrandId: Dispatch<SetStateAction<string>>
}) {

  const t = useTranslations();
  const { branches } = useSelector((state: IRedux) => state.common);
  const [selectedBranch, setSelectedBranch] = useState<ISavedBranch | null>(null);

  const branchRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const BREADCRUMB_DATA = [
    {
      title: t('home'),
      link: '/',
    },
    {
      title: t('branches'),
      link: '/branches',
    },
  ];

  useEffect(() => {
    if (selectedBranch && activeBrandId) {
      handleScrollToBranch(
        selectedBranch,
        branches?.[activeBrandId],
        branchRefs,
        setSelectedBranch
      );
    }
  }, [selectedBranch, branches]);

  return (
    <Container>
      <BreadcrumbComponent data={BREADCRUMB_DATA} />
      <Text as='h1' fontWeight={700} fontSize='24px' mb={4} letterSpacing='-3%'>
        {t('restaurants')}
      </Text>
      <Box mb={6}>
        <SegmentedControl
          size='lg'
          variant='primary'
          height='48px'
          fontWeight={600}
          value={activeBrandId}
          onValueChange={(e) => { if (e.value) setActiveBrandId(e.value) }}
          items={Object.entries(BRAND_NAMES)
            // Hide Yoko Pizza
            .filter(([id]) => id !== FEATURED_BRANDS.yokopizza)
            ?.map(([id, name]) => ({
            label: name,
            value: id,
          }))}
        />
      </Box>
      <Flex
        p={4}
        mb={8}
        gap={3}
        h='90vh'
        w={'full'}
        rounded={24}
        bg='background.card'
      >
        <Box
          h='100%'
          flex={1}
          overflowY='scroll'
          pr={'22px'}
          css={{
            '&::-webkit-scrollbar': {
              width: '4px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#FFFFFF4D',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#1A202433',
              borderRadius: '2px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#1A202466',
            },
            scrollbarWidth: 'thin',
            scrollbarColor: '#1A202433 #FFFFFF4D',
          }}
        >
          <HStack
            gap={2}
            position='sticky'
            top={0}
            bg='background.card'
            zIndex={1}
            pt={1}
            pb={3}
          >
            <InputGroup
              ml={1}
              flex='1'
              endElement={
                search && (
                  <Box
                    rounded={'full'}
                    cursor={'pointer'}
                    onClick={() => setSearch('')}
                  >
                    <ClearIcon />
                  </Box>
                )
              }
              startElement={
                <SearchIcon h='24' w='24'
                  color='#8b8b8b'
                />
              }
            >
              <Input
                ps='44px'
                height='56px'
                rounded={12}
                value={search}
                fontSize='14px'
                bg={'transparent'}
                outlineColor={'primary'}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('location.search_by_branch_name')}
                _placeholder={{ fontSize: '14px', color: 'text.muted' }}
              />
            </InputGroup>
          </HStack>
          <BranchItem
            data={data}
            branchRefs={branchRefs}
            selectedBranch={selectedBranch}
            setSelectedBranch={setSelectedBranch}
            activeShipperId={activeBrandId || ""}
          />
        </Box>
        <Box
          flex={1}
          h='100%'
          rounded={16}
          overflow={'hidden'}
          position={'relative'}
        >
          <BranchesMap
            isSelected={true}
            data={data}
            onClose={() => { }}
            selectedBranch={selectedBranch}
            setSelectedBranch={setSelectedBranch}
            activeShipperId={activeBrandId || ""}
          />
        </Box>
      </Flex>
    </Container>
  );
}
